import React, { useState, useEffect, useRef } from "react";

import { Bell, RefreshCw, X, Megaphone, Search, Inbox } from "lucide-react";
import { createPortal } from "react-dom";

import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchNotifications,
  markNotificationRead,
  deleteNotification,
} from "../../Store/Reducers/NotificationSlice";
import Button from "../Button";

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const {
    notifications,
    notificationsLoading,
    unread,
    lastUpdated,
    markingRead,
    deletingNotification,
  } = useAppSelector(state => state.notification);
  const [searchQuery, setSearchQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [hoveredNotificationId, setHoveredNotificationId] = useState<
    string | null
  >(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Fetch immediately when panel opens
      dispatch(fetchNotifications({}));

      // Fetch notifications every 5 minutes (silently update if data exists)
      const fetchInterval = setInterval(
        () => {
          dispatch(fetchNotifications({}));
        },
        5 * 60 * 1000
      ); // 5 minutes in milliseconds

      return () => {
        clearInterval(fetchInterval);
      };
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await dispatch(fetchNotifications({})).unwrap();
    } catch (error) {
      console.error("Failed to refresh notifications:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleMarkAllRead = () => {
    dispatch(markNotificationRead({ markAll: true }));
  };

  const handleNotificationClick = (
    notificationId: string,
    event: React.MouseEvent
  ) => {
    // Don't mark as read if clicking the delete button
    if ((event.target as HTMLElement).closest(".delete-notification-btn")) {
      return;
    }

    const notification = notifications.find(n => n.id === notificationId);
    if (notification && !notification.isRead) {
      dispatch(markNotificationRead({ id: notificationId }));
    }
  };

  const handleDeleteClick = (
    e: React.MouseEvent,
    notification: { id: string; title: string }
  ) => {
    e.stopPropagation();
    setNotificationToDelete(notification);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!notificationToDelete) return;

    try {
      await dispatch(deleteNotification(notificationToDelete.id)).unwrap();
      setShowDeleteModal(false);
      setNotificationToDelete(null);
      // Refresh notifications after delete
      dispatch(fetchNotifications({}));
    } catch (error) {
      // Error is handled in the reducer
      console.error("Failed to delete notification:", error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setNotificationToDelete(null);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const filteredNotifications = notifications.filter(notification => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      notification.title.toLowerCase().includes(query) ||
      notification.message.toLowerCase().includes(query)
    );
  });

  const filteredCount = searchQuery ? filteredNotifications.length : null;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1200] bg-black/50 transition-opacity duration-300 ease-in-out pointer-events-none">
      <div
        className="fixed top-0 right-0 z-[1201] flex h-screen w-[650px] flex-col bg-white shadow-[-2px_0_8px_rgba(0,0,0,0.1)] pointer-events-auto"
        ref={panelRef}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 m-0">
                Notifications
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="clear"
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-50 transition-colors p-0 min-w-0 bg-white"
                onClick={handleRefresh}
                aria-label="Refresh"
                disabled={isRefreshing}
              >
                <RefreshCw
                  className={`w-5 h-5 text-gray-700 ${
                    isRefreshing ? "animate-spin" : ""
                  }`}
                />
              </Button>
              <Button
                variant="primary"
                className="px-4 py-2 text-sm font-medium bg-blue-500 hover:bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleMarkAllRead}
                disabled={markingRead}
              >
                {markingRead ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Refreshing...
                  </span>
                ) : (
                  "Mark all read"
                )}
              </Button>
              <Button
                variant="clear"
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors p-0 min-w-0"
                onClick={onClose}
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-600" />
              </Button>
            </div>
          </div>
        </div>

        {/* Search and Last Updated Row */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center space-x-1 text-sm text-gray-700">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span className="font-medium">{unread} unread</span>
              </div>
              {filteredCount !== null && (
                <div className="flex items-center space-x-1 text-sm text-blue-600">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="font-medium">{filteredCount} filtered</span>
                </div>
              )}
            </div>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-white pl-10 pr-10 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  aria-label="Clear search"
                  type="button"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {lastUpdated && (
              <div className="text-xs text-gray-500 whitespace-nowrap">
                Last updated: {lastUpdated}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {notificationsLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-3"></div>
              <p className="text-sm text-gray-500">Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 && searchQuery ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Bell className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">
                No matching notifications
              </h3>
              <p className="text-sm text-gray-600 mb-6 text-center px-4">
                No notifications found matching &quot;{searchQuery}&quot;. Try
                adjusting your search terms.
              </p>
              <Button
                variant="clear"
                className="px-4 py-2 text-sm border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-md"
                onClick={() => setSearchQuery("")}
              >
                Clear search
              </Button>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Inbox className="w-16 h-16 text-gray-400 mb-4" />
              <p className="text-sm">No notifications found</p>
            </div>
          ) : (
            <div className="flex flex-col">
              {filteredNotifications.map(notification => (
                <div
                  key={notification.id}
                  className={`flex gap-4 px-6 py-4 cursor-pointer border-b border-gray-200 relative group ${
                    !notification.isRead
                      ? "bg-[#F8F7FC] border-l-4 border-l-purple-400"
                      : "bg-white"
                  }`}
                  onClick={e => handleNotificationClick(notification.id, e)}
                  onMouseEnter={() => setHoveredNotificationId(notification.id)}
                  onMouseLeave={() => setHoveredNotificationId(null)}
                >
                  <div className="relative flex-shrink-0">
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-full ${
                        !notification.isRead ? "bg-purple-200" : "bg-purple-100"
                      }`}
                    >
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                        <Megaphone className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    {!notification.isRead && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>
                    )}
                  </div>
                  <div className="flex-1 flex flex-col gap-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 m-0">
                      {notification.title}
                    </h3>
                    <p className="text-sm text-gray-700 m-0 leading-relaxed break-words">
                      {notification.message}
                    </p>
                    <span className="text-xs text-gray-500 mt-1">
                      {formatTimeAgo(notification.createdAt)}
                    </span>
                  </div>
                  {/* Delete button - shown on hover */}
                  {hoveredNotificationId === notification.id && (
                    <button
                      className="delete-notification-btn absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors z-10"
                      onClick={e =>
                        handleDeleteClick(e, {
                          id: notification.id,
                          title: notification.title,
                        })
                      }
                      aria-label="Delete notification"
                      type="button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal - Inside panel container */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-[1202] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6">
                {/* Custom Header */}
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <X className="w-5 h-5 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Delete Notification
                    </h3>
                  </div>
                  <button
                    onClick={handleCancelDelete}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Close"
                    type="button"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Warning Message */}
                <p className="text-sm text-gray-500 mb-2">
                  This action cannot be undone.
                </p>

                {/* Confirmation Question */}
                <p className="text-sm text-gray-700 mb-6">
                  Are you sure you want to delete this notification?{" "}
                  <strong>&quot;{notificationToDelete?.title}&quot;</strong>
                </p>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={handleCancelDelete}>
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleConfirmDelete}
                    disabled={deletingNotification}
                    loading={deletingNotification}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default NotificationPanel;
